import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './../services/auth.service';
import { DemoMaterialModule } from 'src/app/demo-material-module';
import { AfterViewInit, Component, DestroyRef, ElementRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIf } from '@angular/common';
import { NgForm, FormsModule } from '@angular/forms';
import { GymConfigService } from '../services/gym-config.service';
import { BrandingService } from '../services/branding.service';
import { DEFAULT_BRAND_NAME } from '../models/branding.model';
import { finalize, switchMap } from 'rxjs';
import { AppConfigService } from '../services/app-config.service';
import { FALLBACK_ROUTE } from '../auth/route-access';

const PAGE_CONFIG_TYPE = 'PAGE';
const ADMIN = 'ADMIN';
const DASHBOARD = 'dashboard';

export interface PageElement {
  id: number;
  name: string;
  type: string;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-fit-login',
  templateUrl: './fit-login.component.html',
  imports: [DemoMaterialModule, FormsModule, NgIf],
  standalone: true,
  styleUrls: ['./fit-login.component.scss'],
})
export class FitLoginComponent implements AfterViewInit {
  @ViewChild('myForm') myForm!: NgForm;
  @ViewChild('usernameInput') usernameInput!: ElementRef<HTMLInputElement>;

  private readonly destroyRef = inject(DestroyRef);

  username: string = '';
  password: string = '';

  /** Brand resolved by the backend (public branding endpoints). */
  brandLogoUrl = this.brandingService.logoUrl;
  brandName = DEFAULT_BRAND_NAME;

  /** Estado de UI del formulario. */
  hidePassword = true;
  capsLockOn = false;
  loading = false;
  errorMessage = '';

  get appVersion(): string {
    return this.appConfigService.version;
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private readonly gymConfigService: GymConfigService,
    private readonly brandingService: BrandingService,
    private readonly appConfigService: AppConfigService,
  ) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.usernameInput?.nativeElement?.focus());
    this.brandingService
      .getInfo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(info => (this.brandName = info.name));
  }

  /** Muestra el aviso de mayusculas activadas mientras se escribe la contraseña. */
  checkCapsLock(event: KeyboardEvent): void {
    this.capsLockOn = event.getModifierState?.('CapsLock') ?? false;
  }

  login() {
    if (this.loading) {
      return;
    }

    if (!this.myForm?.valid) {
      this.myForm?.control.markAllAsTouched();
      this.errorMessage = 'Completa usuario y contraseña para continuar.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authService
      .login(this.username, this.password)
      .pipe(
        switchMap(() => {
          return this.gymConfigService.getGymConfigByType(PAGE_CONFIG_TYPE);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (configurablePages: PageElement[]) => {
          const userRole = this.authService.getUserRole() ?? 'STAFF';
          const dashboardConfig = configurablePages.find(current => current.name === DASHBOARD) ?? null;
          const landingRoute = dashboardConfig?.isEnabled && userRole === ADMIN ? '/dashboard' : `/${FALLBACK_ROUTE}`;
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
          const isUsableReturnUrl = returnUrl?.startsWith('/') && !returnUrl.startsWith('//') && returnUrl !== '/';
          const destination = isUsableReturnUrl && returnUrl ? returnUrl : landingRoute;

          void this.router.navigateByUrl(destination, { replaceUrl: true });
        },
        error: error => {
          console.error('Error al autenticar:', error);
          this.errorMessage = this.resolveErrorMessage(error?.status);
          this.toastr.error(this.errorMessage, 'Error');
        },
      });
  }

  private resolveErrorMessage(status?: number): string {
    if (status === 0) {
      return 'Sin conexión con el servidor. Revisa tu red e intenta de nuevo.';
    }
    if (status && status >= 500) {
      return 'El servidor no respondió correctamente. Intenta en unos segundos.';
    }
    return 'Usuario o contraseña incorrectas';
  }
}
